/*
    Author: draplater
    Create Time: 17-8-7
    Project: verbnet2017
*/

// This module deal with structure of frame


import {Sentence} from "./verb";
import {colors} from "../utils";

export type ColorMapType = (tag: string) => string | undefined;

export interface Tag {
    name: string,
    definition: string,
    lemmas: Map<string, string[]>,
    samples: Sentence[],
    type: string
}

export class ExampleSentences {
    public name: string;
    public count: number;
    public example: string[];

    constructor(fields) {
        Object.assign(this, fields);
    }
}

export interface LexiconMetadata {
    name: string;
    frequency: string | undefined;
}

export class FrameMeta {
    public name: string;
    public def?: string;
    public schema?: string[];
    public sub_frames: FrameMeta[];
    public lexicons: string[];
    public lexiconMetadata: {[key: string]: LexiconMetadata;};
    public totalCount?: number;
    public sentences: ExampleSentences[];

    constructor(fields) {
        Object.assign(this, fields);
    }

    static fromJSON(fields): FrameMeta {
        const ret = new FrameMeta(fields);
        ret.sub_frames = ret.sub_frames.map(x => FrameMeta.fromJSON(x));
        return ret;
    }

    static wrapTop(frames: FrameMeta[]): FrameMeta {
        return new FrameMeta({
            name: "",
            sub_frames: frames,
            lexicons: [] as string[],
            sentences: [] as ExampleSentences[]
        });
    }

    find(path: string[]): FrameMeta | undefined {
        /* find sub frame by path */
        if (path.length == 0) {
            return this;
        } else {
            const expectedFrames = this.sub_frames.filter(x => x.name == path[0]);
            if (expectedFrames.length) {
                return expectedFrames[0].find(path.slice(1));
            } else {
                return undefined;
            }
        }
    }

    findLexiconMeta(path: string[]): LexiconMetadata | undefined {
        console.log(path.slice(0, -1));
        const parentFrame = this.find(path.slice(0, -1));
        if(parentFrame === undefined) {
            return undefined;
        } else {
            console.log(parentFrame);
            return parentFrame.lexiconMetadata[path.slice(-1)[0]];
        }
    }
}


class TXTParserError extends Error {
    constructor(message) {
        super(message);
        Object.setPrototypeOf(this, Error.prototype);
    }
}

export interface SemiLexicalizedCollocates {
    name: string;
    tag: string;
    verbs: string[];
}

export class FrameInfo {
    name: string;
    type: string | undefined = undefined;
    definition: string;
    verb: string;
    pattern: string = "*.tag";
    lemmas: Map<string, string[]> = new Map();
    subFrames: Map<string, string[]> = new Map();
    semiLexicalized: Map<string, SemiLexicalizedCollocates> = new Map();
    samples: Sentence[] = [];
    tags: Tag[] = [];
    tagTypes: string[] = [];

    constructor(name: string) {
        this.name = name;
    }

    colorFunc = (tag: string) => {
        /* tag => color */
        if(this === undefined) {
            console.log("Frame undefined.");
            return undefined;
        } else if(this.tags.map(x => x.name).indexOf(tag) >= 0) {
            return tag.startsWith("*")?colors[2]:colors[1];
        } else {
            return colors[0];
        }
    };

    static parseLemmaOrSubFrame(
        currentObj: FrameInfo | Tag, field: "lemmas" | "subFrames",
        idx: number, value: string): void {
        if(!value) {
            return;
        }
        const re = /^(?:\[(.*?)])?\s*(.*?)$/;
        const m = re.exec(value);
        if (m != null && m[2]) {
            currentObj[field].set(m[1] || "", m[2].split(","));
        } else {
            throw new TXTParserError(`Invalid Lemma in line ${idx}`);
        }
    }

    static fromTxtFormat(content: string): FrameInfo {
        const frameFields = ["Tag", "Frame type", "Verb", "subframes",
            "Semi-lexicalized collocates", "Pattern"];
        const commonFields = ["Def", "Def.", "Lemma", "Sample"];
        const lines = content.split("\n");
        let tagType = "";
        let currentFrameObj: FrameInfo | undefined = undefined;
        let currentTagObj: Tag | undefined = undefined;
        let currentObj: FrameInfo | Tag | undefined = undefined;
        let currentKey: string | undefined = undefined;
        for (let idx=1; idx<=lines.length; idx++) {
            const line = lines[idx-1];
            const splitPoint = line.indexOf(":");
            if (splitPoint > 0) {
                // This line is attr-value pair
                currentKey = line.slice(0, splitPoint).trim();
                let value = line.slice(splitPoint + 1).trim();
                if (currentKey == "Frame") {
                    // is Frame definition
                    currentObj = currentFrameObj = new FrameInfo(value);
                }
                else if (frameFields.indexOf(currentKey) >= 0) {
                    // is attr of frame
                    if (currentFrameObj === undefined) {
                        throw new TXTParserError(`Frame is not defined in line ${idx}`);
                    }
                    if (currentKey == "Tag") {
                        // is tag definition
                        const samples: Sentence[] = [];
                        currentObj = currentTagObj = {
                            type: tagType,
                            name: value,
                            definition: "",
                            lemmas: new Map(),
                            samples
                        };
                        currentFrameObj.tags.push(currentObj);
                    } else if (currentKey == "subframes") {
                        // is verb
                        FrameInfo.parseLemmaOrSubFrame(
                            currentFrameObj, "subFrames", idx, value);
                    } else if (currentKey == "Verb") {
                        // is verb
                        currentFrameObj.verb = value;
                    } else if (currentKey == "Frame type") {
                        // is verb
                        currentFrameObj.type = value;
                    } else if (currentKey == "Pattern") {
                        // is pattern
                        currentFrameObj.pattern = value;
                    } else if (currentKey === "Semi-lexicalized collocates") {
                        const re = /^(?:\[(.*?)])?\s*(?:\[(.*?)])?(.*?)$/;
                        const m = re.exec(value);
                        if (m != null) {
                            currentFrameObj.semiLexicalized.set(m[2] || "",
                                {
                                    tag: m[1],
                                    name: m[2],
                                    verbs: m[3].split(",")
                                });
                        } else {
                            throw new TXTParserError(`Invalid Lemma in line ${idx}`);
                        }
                    }
                } else if (commonFields.indexOf(currentKey) >= 0) {
                    // is attr of frame of tag
                    if (currentObj === undefined) {
                        throw new TXTParserError(`Frame or tag is not defined in line ${idx}`);
                    }
                    if (currentKey == "Def" || currentKey == "Def.") {
                        currentObj.definition = value;
                    } else if (currentKey == "Lemma") {
                        FrameInfo.parseLemmaOrSubFrame(currentObj, "lemmas", idx, value);
                    } else if (currentKey == "Sample") {
                        if (value) {
                            currentObj.samples.push(Sentence.parseSentence(value));
                        }
                    }
                }
            } else {
                const lineTrim = line.trim();
                if (!lineTrim) {
                    // is empty line, do nothing
                } else if (line.startsWith("    ")) {
                    // is continue line
                    if (currentObj === undefined) {
                        throw new TXTParserError(`Frame is not defined in line ${idx}`);
                    }
                    if (currentKey === undefined) {
                        throw new TXTParserError(`Invalid continue in line ${idx}`);
                    }
                    if (currentKey == "Sample") {
                        currentObj.samples.push(Sentence.parseSentence(line.trim()));
                    } else if (currentKey == "subframes") {
                        // is verb
                        FrameInfo.parseLemmaOrSubFrame(
                            currentObj, "subFrames", idx, lineTrim);
                    } else if (currentKey == "Lemma") {
                        FrameInfo.parseLemmaOrSubFrame(currentObj, "lemmas", idx, lineTrim);
                    } else if (currentKey == "Semi-lexicalized collocates") {
                        const re = /^(?:\[(.*?)])?\s*(?:\[(.*?)])?(.*?)$/;
                        const m = re.exec(lineTrim);
                        if (m != null) {
                            if (!(currentObj instanceof FrameInfo)) {
                                throw new TXTParserError(`Semi-lexicalized only exist on frames(line ${idx})`);
                            }
                            currentObj.semiLexicalized.set(m[2] || "",
                                {
                                    tag: m[1],
                                    name: m[2],
                                    verbs: m[3].split(",")
                                });
                        } else {
                            throw new TXTParserError(`Invalid Lemma in line ${idx}`);
                        }
                    }
                } else if (lineTrim.startsWith("[") && lineTrim.endsWith("]")) {
                    // is tag type
                    if (currentFrameObj === undefined) {
                        throw new TXTParserError(`Frame is not defined in line ${idx}.`);
                    }
                    tagType = lineTrim.slice(1, -1);
                    currentFrameObj.tagTypes.push(tagType);
                }
            }
        }
        if (currentFrameObj === undefined) {
            throw new TXTParserError(`No frame defined.`);
        }
        return currentFrameObj;
    }

    getCombinedTags(): Tag[] {
        return [{
            name: this.name, definition: this.definition,
            lemmas: this.lemmas, type: "Frame", samples: this.samples
        },
            ...this.tags];
    }

    getColorMap(): ColorMapType {
        return this.colorFunc;
    }
}

export const defaultColorMap = (x) => undefined;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/problem-domain/frame.ts